
import JSZip from 'jszip';

export interface CompressionResult {
  compressedFile: File;
  compressedUrl: string;
  originalSize: number;
  compressedSize: number;
  compressionRatio: number;
}

export interface CompressionOptions {
  quality?: number;
  maxWidth?: number;
  maxHeight?: number;
  autoResize?: boolean;
}

export const compressImage = async (
  file: File, 
  quality: number = 0.8, 
  options?: CompressionOptions
): Promise<CompressionResult> => {
  console.log('🔄 Iniciando compresión de:', file.name, 'Tamaño original:', file.size, 'bytes');
  console.log('📝 Configuración:', { quality, options });

  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();

    if (!ctx) {
      console.error('❌ Error: No se pudo obtener el contexto del canvas');
      reject(new Error('No se pudo crear el contexto del canvas'));
      return;
    }

    img.onload = async () => {
      console.log('✅ Imagen cargada correctamente. Dimensiones originales:', img.width, 'x', img.height);
      
      let { width, height } = img;

      // Aplicar redimensionamiento si está habilitado
      if (options?.autoResize) {
        const maxWidth = options.maxWidth || width;
        const maxHeight = options.maxHeight || height;

        if (width > maxWidth || height > maxHeight) {
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
          console.log('🔄 Redimensionando a:', width, 'x', height, 'ratio:', ratio);
        }
      }

      // Configurar el canvas con las dimensiones finales
      canvas.width = width;
      canvas.height = height;
      console.log('🎨 Canvas configurado con dimensiones:', width, 'x', height);

      try {
        // Dibujar la imagen redimensionada
        ctx.drawImage(img, 0, 0, width, height);
        console.log('✅ Imagen dibujada en el canvas');

        const isPng = file.type === 'image/png';
        const isWebP = file.type === 'image/webp';

        // Helper para convertir canvas.toBlob en Promise
        const canvasToBlob = (mimeType: string, q?: number): Promise<Blob> =>
          new Promise((res, rej) => {
            canvas.toBlob(
              (b) => (b ? res(b) : rej(new Error('No se pudo crear el blob'))),
              mimeType,
              q
            );
          });

        let bestBlob: Blob;
        let bestType: string;

        if (isPng) {
          // Doble intento: PNG y JPEG, elegir el menor
          const [pngBlob, jpegBlob] = await Promise.all([
            canvasToBlob('image/png'),
            canvasToBlob('image/jpeg', quality),
          ]);
          console.log('📊 PNG blob:', pngBlob.size, 'bytes | JPEG blob:', jpegBlob.size, 'bytes');
          if (pngBlob.size <= jpegBlob.size) {
            bestBlob = pngBlob;
            bestType = 'image/png';
          } else {
            bestBlob = jpegBlob;
            bestType = 'image/jpeg';
          }
        } else if (isWebP) {
          bestBlob = await canvasToBlob('image/webp', quality);
          bestType = 'image/webp';
        } else {
          bestBlob = await canvasToBlob('image/jpeg', quality);
          bestType = 'image/jpeg';
        }

        console.log('✅ Mejor formato elegido:', bestType, 'Tamaño:', bestBlob.size, 'bytes');

        // Garantía de no-aumento: si el comprimido es mayor, devolver original
        if (bestBlob.size >= file.size) {
          console.log('⚠️ Comprimido >= original. Devolviendo archivo original sin cambios.');
          const originalUrl = URL.createObjectURL(file);
          resolve({
            compressedFile: file,
            compressedUrl: originalUrl,
            originalSize: file.size,
            compressedSize: file.size,
            compressionRatio: 0,
          });
          return;
        }

        // Ajustar extensión del nombre si cambió de formato
        let outputName = file.name;
        if (bestType === 'image/jpeg' && isPng) {
          outputName = file.name.replace(/\.png$/i, '.jpg');
        }

        const compressedFile = new File([bestBlob], outputName, {
          type: bestType,
          lastModified: Date.now(),
        });

        const compressedUrl = URL.createObjectURL(bestBlob);
        const originalSize = file.size;
        const compressedSize = bestBlob.size;
        const compressionRatio = ((originalSize - compressedSize) / originalSize) * 100;

        console.log('🎉 Compresión completada:');
        console.log('📊 Tamaño original:', originalSize, 'bytes');
        console.log('📊 Tamaño comprimido:', compressedSize, 'bytes');
        console.log('📊 Reducción:', compressionRatio.toFixed(1) + '%');

        resolve({
          compressedFile,
          compressedUrl,
          originalSize,
          compressedSize,
          compressionRatio,
        });
      } catch (error) {
        console.error('❌ Error al procesar la imagen:', error);
        reject(new Error('Error al procesar la imagen: ' + (error instanceof Error ? error.message : 'Error desconocido')));
      }
    };

    img.onerror = (error) => {
      console.error('❌ Error al cargar la imagen:', error);
      reject(new Error('Error al cargar la imagen. Verifica que sea un archivo JPEG o PNG válido.'));
    };

    try {
      console.log('🔄 Creando URL del objeto para cargar la imagen...');
      img.src = URL.createObjectURL(file);
    } catch (error) {
      console.error('❌ Error al crear URL del objeto:', error);
      reject(new Error('Error al crear la URL de la imagen: ' + (error instanceof Error ? error.message : 'Error desconocido')));
    }
  });
};

export const downloadFile = (file: File, filename?: string) => {
  const url = URL.createObjectURL(file);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || file.name;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const downloadFilesAsZip = async (files: File[], zipName: string = 'compressed_images.zip') => {
  const zip = new JSZip();
  
  // Agregar cada archivo al ZIP
  files.forEach((file, index) => {
    const fileName = file.name || `image_${index + 1}.jpg`;
    zip.file(fileName, file);
  });
  
  try {
    // Generar el archivo ZIP
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    
    // Crear un enlace temporal para descargar
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = zipName;
    
    // Usar la API showDirectoryPicker si está disponible (Chrome/Edge)
    if ('showDirectoryPicker' in window) {
      try {
        const directoryHandle = await (window as any).showDirectoryPicker();
        const fileHandle = await directoryHandle.getFileHandle(zipName, { create: true });
        const writable = await fileHandle.createWritable();
        await writable.write(zipBlob);
        await writable.close();
        return;
      } catch (error) {
        // Si el usuario cancela o hay un error, usar descarga normal
        console.log('Usando descarga normal debido a:', error);
      }
    }
    
    // Descarga normal si no hay soporte para selección de carpeta
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    
  } catch (error) {
    console.error('Error al crear el archivo ZIP:', error);
    throw error;
  }
};
